import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import type { WorkoutTemplate } from '@/types';
import { Button } from '@/components/ui';
import { TemplatePicker } from './TemplatePicker';
import { CARDS } from '@/styles/classNames';

interface TrainingPlanSectionProps {
  rotationTemplateIds: string[];
  lastCompletedTemplateId?: string;
  dailyRotationTemplateIds: string[];
  lastCompletedDailyTemplateId?: string;
  /** All trainer templates (library + cloned) for lookup */
  allTemplates: WorkoutTemplate[];
  /** Trainer's library templates for picker */
  libraryTemplates: WorkoutTemplate[];
  onAddToRotation: (templateId: string) => Promise<string | void>;
  onRemoveFromRotation: (clonedTemplateId: string) => Promise<void>;
  onReorderRotation: (oldIndex: number, newIndex: number) => Promise<void>;
  onAddToDailyRotation: (templateId: string) => Promise<string | void>;
  onRemoveFromDailyRotation: (clonedTemplateId: string) => Promise<void>;
  onReorderDailyRotation: (oldIndex: number, newIndex: number) => Promise<void>;
  onEditTemplate?: (templateId: string) => void;
  loading?: boolean;
}

interface SortableRotationItemProps {
  id: string;
  index: number;
  templateName: string;
  exerciseCount: number;
  isNext: boolean;
  testIdPrefix: string;
  onEdit?: () => void;
  onRemove: () => void;
}

function SortableRotationItem({
  id,
  index,
  templateName,
  exerciseCount,
  isNext,
  testIdPrefix,
  onEdit,
  onRemove,
}: SortableRotationItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`${testIdPrefix}-item`}
      data-template-id={id}
      className={`${CARDS.nested} flex items-center gap-2 p-2 md:p-3`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing shrink-0 w-8 h-8 md:w-6 md:h-6 flex items-center justify-center rounded-md border border-gray-200 dark:border-white/10 glass-hover text-base md:text-xs touch-none"
        aria-label="Drag to reorder"
      >
        ⠿
      </button>

      <div className="flex-1 min-w-0">
        <p className="font-medium truncate text-gray-900 dark:text-white">
          {index + 1}. {templateName}
        </p>
        <p className="text-xs text-slate-400">
          {exerciseCount} exercise{exerciseCount !== 1 ? 's' : ''}
          {isNext && (
            <span className="ml-2 text-primary-400 font-medium">Up next</span>
          )}
        </p>
      </div>

      <div className="shrink-0 flex items-center gap-1">
        {onEdit && (
          <button
            onClick={onEdit}
            data-testid={`${testIdPrefix}-edit`}
            className="text-xs text-slate-300 hover:text-white px-2 py-1"
            aria-label="Edit template"
          >
            Edit
          </button>
        )}
        <button
          onClick={onRemove}
          data-testid={`${testIdPrefix}-remove`}
          className="text-xs text-red-300 hover:text-red-200 px-2 py-1"
          aria-label="Remove template"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

interface RotationListSectionProps {
  title: string;
  description: string;
  templateIds: string[];
  lastCompletedTemplateId?: string;
  templateLookup: Map<string, WorkoutTemplate>;
  onAdd: (templateId: string) => Promise<string | void>;
  onRemove: (clonedTemplateId: string) => Promise<void>;
  onReorder: (oldIndex: number, newIndex: number) => Promise<void>;
  onEditTemplate?: (templateId: string) => void;
  pickerTitle: string;
  libraryTemplates: WorkoutTemplate[];
  saving: boolean;
  setSaving: (saving: boolean) => void;
  testIdPrefix: string;
}

function RotationListSection({
  title,
  description,
  templateIds,
  lastCompletedTemplateId,
  templateLookup,
  onAdd,
  onRemove,
  onReorder,
  onEditTemplate,
  pickerTitle,
  libraryTemplates,
  saving,
  setSaving,
  testIdPrefix,
}: RotationListSectionProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nextIndex = (() => {
    if (templateIds.length === 0) return -1;
    if (!lastCompletedTemplateId) return 0;
    const lastIdx = templateIds.indexOf(lastCompletedTemplateId);
    if (lastIdx === -1) return 0;
    return (lastIdx + 1) % templateIds.length;
  })();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = templateIds.indexOf(active.id as string);
    const newIndex = templateIds.indexOf(over.id as string);
    if (oldIndex !== -1 && newIndex !== -1) {
      setSaving(true);
      setError(null);
      try {
        await onReorder(oldIndex, newIndex);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not reorder templates.');
      } finally {
        setSaving(false);
      }
    }
  };

  const handlePickerSelect = async (templateId: string) => {
    setSaving(true);
    setError(null);
    try {
      await onAdd(templateId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add this template.');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (clonedId: string) => {
    const templateName = templateLookup.get(clonedId)?.name || 'this template';
    const confirmed = window.confirm(`Remove ${templateName} from this rotation?`);
    if (!confirmed) return;

    setSaving(true);
    setError(null);
    try {
      await onRemove(clonedId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove this template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400">{title}</p>
          <p className="text-sm text-slate-300">{description}</p>
        </div>
        <span className="text-xs text-slate-400">
          {templateIds.length} template{templateIds.length !== 1 ? 's' : ''}
        </span>
      </div>

      {templateIds.length > 0 && nextIndex >= 0 && (
        <p className="text-sm text-slate-300">
          Next workout:{' '}
          <span className="text-primary-400 font-medium">
            {templateLookup.get(templateIds[nextIndex])?.name || 'Unknown'}
          </span>
          {' '}({nextIndex + 1} of {templateIds.length})
        </p>
      )}

      {error && (
        <p className="rounded-md border border-red-300/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      {templateIds.length > 0 ? (
        <div className="space-y-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={templateIds}
              strategy={verticalListSortingStrategy}
            >
              {templateIds.map((id, idx) => {
                const tpl = templateLookup.get(id);
                return (
                  <SortableRotationItem
                    key={id}
                    id={id}
                    index={idx}
                    templateName={tpl?.name || 'Unknown template'}
                    exerciseCount={tpl?.items?.length || 0}
                    isNext={idx === nextIndex}
                    testIdPrefix={testIdPrefix}
                    onEdit={onEditTemplate ? () => onEditTemplate(id) : undefined}
                    onRemove={() => handleRemove(id)}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <div className="text-center py-6 rounded-lg text-slate-400">
          <p className="text-sm">No templates yet</p>
          <p className="text-xs mt-1">Add templates to build this rotation</p>
        </div>
      )}

      <Button
        variant="secondary"
        size="sm"
        onClick={() => setPickerOpen(true)}
        disabled={saving}
        data-testid={`${testIdPrefix}-add-button`}
        className="w-full"
      >
        {pickerTitle}
      </Button>

      <TemplatePicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
        templates={libraryTemplates}
        title={pickerTitle}
      />
    </div>
  );
}

export function TrainingPlanSection({
  rotationTemplateIds,
  lastCompletedTemplateId,
  dailyRotationTemplateIds,
  lastCompletedDailyTemplateId,
  allTemplates,
  libraryTemplates,
  onAddToRotation,
  onRemoveFromRotation,
  onReorderRotation,
  onAddToDailyRotation,
  onRemoveFromDailyRotation,
  onReorderDailyRotation,
  onEditTemplate,
  loading,
}: TrainingPlanSectionProps) {
  const [saving, setSaving] = useState(false);
  const templateLookup = new Map(allTemplates.map((t) => [t.id, t]));

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-2xl p-4 border border-white/10 space-y-3"
      >
        <h2 className="text-xl font-semibold">Training plan</h2>
        <p className="text-sm text-slate-400">Loading...</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl p-4 border border-white/10 space-y-6"
    >
      <h2 className="text-xl font-semibold">Training plan</h2>
      <p className="text-sm text-slate-300">
        Manage the templates this client sees in their workout plan. Added templates appear
        immediately in the lists below, where they can be edited, reordered, or removed.
      </p>

      <RotationListSection
        title="Session workouts"
        description="Longer programmed workouts for lifting, rehab, or dedicated training sessions."
        templateIds={rotationTemplateIds}
        lastCompletedTemplateId={lastCompletedTemplateId}
        templateLookup={templateLookup}
        onAdd={onAddToRotation}
        onRemove={onRemoveFromRotation}
        onReorder={onReorderRotation}
        onEditTemplate={onEditTemplate}
        pickerTitle="Add session workout"
        libraryTemplates={libraryTemplates}
        saving={saving}
        setSaving={setSaving}
        testIdPrefix="scheduled-rotation"
      />

      <RotationListSection
        title="Daily workouts"
        description="Short mobility, stretching, warmup, or get-moving workouts for the daily card."
        templateIds={dailyRotationTemplateIds}
        lastCompletedTemplateId={lastCompletedDailyTemplateId}
        templateLookup={templateLookup}
        onAdd={onAddToDailyRotation}
        onRemove={onRemoveFromDailyRotation}
        onReorder={onReorderDailyRotation}
        onEditTemplate={onEditTemplate}
        pickerTitle="Add daily workout"
        libraryTemplates={libraryTemplates}
        saving={saving}
        setSaving={setSaving}
        testIdPrefix="daily-rotation"
      />
    </motion.div>
  );
}
